import "antd/dist/antd.css";

import { Modal } from "antd";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import AppContent from "../../components/AppContent";
import { formatCardNumber } from "../../components/CardNumber/utils";
import State from "../../State";
import intl from "../../utils/intl";
import * as actions from "./actions";
import FuelingProcessCancelEdit from "./components/FuelingProcessCancelEdit";
import * as cancelEditActions from "./components/FuelingProcessCancelEdit/actions";
import FuelingProcessDetail from "./components/FuelingProcessDetail";
import FuelingProcessFilter from "./components/FuelingProcessFilter";
import { FilterValues } from "./components/FuelingProcessFilter/props";
import FuelingProcessReceiptEdit from "./components/FuelingProcessReceiptEdit";
import * as receiptEditActions from "./components/FuelingProcessReceiptEdit/actions";
import FuelingProcessTable from "./components/FuelingProcessTable";
import useQuery from "./hooks";
import messages from "./messages";
import FuelingProcessesPageProps from "./props";

const FuelingProcessLayout = styled(AppContent)`
  padding: 30px;
`;

const FuelingProcessWrapper = styled.div`
  padding-top: 20px;
`;

const FuelingProcessPage: React.FC = () => {
  const [readFromQuery, setReadFromQuery] = useState(false);
  const [modalVisible, setVisibilityForModal] = useState(false);

  const dispatch = useDispatch();

  const { tableProps, detailProps } = useSelector<
    State,
    FuelingProcessesPageProps
  >((state) => {
    return {
      tableProps: {
        loading: state.processes.tableState.loading,
        processes: state.processes.tableState.processes.map((process) => {
          return {
            fuelingProcessId: process.fuelingProcessId,
            cardNumber: formatCardNumber(process.cardNumber),
            createTime: process.createTime,
            state: process.state,
            station: process.station.stationId,
            vendor: process.station.vendor,
            productId: process.productId,
            requestedAmount: process.requestedAmount,
            receiptPrice: process.receipt?.price,
            receiptAmount: process.receipt?.totalAmount,
            partnerReceiptPrice: process.partnerReceipt?.price,
            partnerReceiptAmount: process.partnerReceipt?.totalAmount,
            quantity: process.receipt?.quantity,
            rejectionReason: process.rejection?.code,
            rejectionDescription: process.rejection?.description,
            clientId: process.clientId,
            partnerId: process.partnerId,
            partnerRefuelingId: process.partnerRefuelingId,
            supplierId: process.station.supplierId,
            batch: process.batch,
            testMode: process.testMode,
            initialPrice: process.initialPrice,
            partnerInitialPrice: process.partnerInitialPrice,
          };
        }),
        filter: state.processes.tableState.filter,
        viewDetail: (fuelingProcessId: string): void => {
          dispatch(actions.loadFuelingProcessDetail(fuelingProcessId));
          setVisibilityForModal(true);
        },
        exportToExcel: (values: FilterValues): void => {
          dispatch(actions.exportFuelingProcessesToExcel(values));
        },
        exportingToExcel: state.processes.exportToExcelState.exportingToExcel,
        downloadUrl: state.processes.exportToExcelState.downloadUrl,
      },
      detailProps: {
        loading: state.processes.detailState.loading,
        fuelingProcess: state.processes.detailState.fuelingProcess,
        onSetReceipt: () => {
          if (!state.processes.detailState.fuelingProcess) return;

          setVisibilityForModal(false);
          dispatch(
            receiptEditActions.showFuelingProcessReceiptEdit(
              state.processes.detailState.fuelingProcess
            )
          );
        },
        onCancelProcess: () => {
          if (!state.processes.detailState.fuelingProcess) return;

          setVisibilityForModal(false);
          dispatch(
            cancelEditActions.showFuelingProcessCancelEdit(
              state.processes.detailState.fuelingProcess.details
                .fuelingProcessId
            )
          );
        },
      },
    };
  });

  const search = (values: FilterValues): void => {
    dispatch(
      actions.loadFuelingProcesses({
        cardNumber: values.cardNumber,
        dateTimeFrom: values.dateTimeFrom,
        dateTimeTill: values.dateTimeTill,
        stationId: values.stationId,
      })
    );
  };

  const closeModal = (): void => {
    setVisibilityForModal(false);
  };

  const query = useQuery();
  const cardNumberFromQuery = query.get("cardNumber");
  if (!readFromQuery && cardNumberFromQuery != null) {
    tableProps.filter = {
      cardNumber: cardNumberFromQuery,
    };
  } else if (
    tableProps.filter.cardNumber === undefined &&
    tableProps.filter.dateTimeFrom === undefined &&
    tableProps.filter.dateTimeTill === undefined &&
    tableProps.filter.stationId === undefined
  ) {
    tableProps.filter = {
      dateTimeFrom: moment(),
    };
  }

  useEffect(() => {
    if (!readFromQuery) {
      setReadFromQuery(true);
      if (cardNumberFromQuery != null) {
        dispatch(actions.loadFuelingProcesses(tableProps.filter));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Helmet>
        <title>{intl.formatMessage(messages.header)}</title>
      </Helmet>
      <FuelingProcessLayout>
        <FuelingProcessFilter
          loading={tableProps.loading}
          search={search}
          filter={tableProps.filter}
          exportToExcel={tableProps.exportToExcel}
          exportingToExcel={tableProps.exportingToExcel}
          downloadUrl={tableProps.downloadUrl}
        />
      </FuelingProcessLayout>

      <FuelingProcessWrapper>
        <FuelingProcessLayout>
          <FuelingProcessTable {...tableProps} />
        </FuelingProcessLayout>
        <Modal
          width={640}
          onCancel={closeModal}
          cancelButtonProps={{ style: { display: "none" } }}
          okButtonProps={{ style: { display: "none" } }}
          visible={modalVisible}
        >
          <FuelingProcessDetail
            loading={detailProps.loading}
            fuelingProcess={detailProps.fuelingProcess}
            onSetReceipt={detailProps.onSetReceipt}
            onCancelProcess={detailProps.onCancelProcess}
          />
        </Modal>
        <FuelingProcessReceiptEdit></FuelingProcessReceiptEdit>
        <FuelingProcessCancelEdit></FuelingProcessCancelEdit>
      </FuelingProcessWrapper>
    </>
  );
};

export default FuelingProcessPage;
